/**
 * Possession: where a work physically is, and who has it out on loan.
 *
 * These are two facts and not one. Where a work sits says nothing about who owns
 * it, and neither does a loan — ownership is a `collection_grant`, and moving it
 * is a transfer, which this registry still cannot express. See "Transfers" under
 * Deliberately absent in docs/accessFGARules.md.
 *
 * Must stay in step with `ondo.location_kind` and `ondo.loan_status`, both
 * declared in 20260804090200_locations_loans.sql. Nothing enforces it but
 * this comment.
 */
/**
 * Where a work is.
 *
 * Deliberately no `on_loan`. A work on loan is still somewhere — hanging in the
 * borrower's gallery, or in their store — and putting the loan in here would
 * make the location table and the loan table two sources of truth for the same
 * fact, free to disagree. Being on loan is derived from a live loan and nothing
 * else.
 */
export const LOCATION_KINDS = ["displayed", "in_storage", "in_transit"];
/**
 * A loan's life.
 *
 * `proposed` exists only for borrowers who are on ONDO: asserting unilaterally
 * that an institution holds your painting is a claim about that institution. A
 * loan to a venue with no ONDO id opens `agreed`, because there is nobody to ask.
 *
 * `declined` is the BORROWER's refusal and `cancelled` is the LENDER's, and
 * they are two words because they name two parties. A refusal stored as a
 * cancellation would have had every reader of a status — this screen's badge,
 * the estate rail, `ondo.owner_loans()` — say the lender changed their mind at
 * the moment a museum said no. 20261003094000 decision 1.
 */
export const LOAN_STATUSES = [
    "proposed",
    "agreed",
    "declined",
    "out",
    "returned",
    "cancelled",
];
/**
 * Still live: it occupies the work's one loan slot.
 *
 * `declined` is not live, which is why a refused proposal frees the work to be
 * offered elsewhere. It mirrors `loans_one_live`, unique on `(work_id) where
 * status in ('proposed', 'agreed', 'out')` — and that index is the authority,
 * not this function.
 */
export function isLiveLoan(status) {
    return status === "proposed" || status === "agreed" || status === "out";
}
/**
 * Which act of a loan one history entry records.
 *
 * Must stay in step with `ondo.loan_history_act`, declared in
 * 20261003094000_borrower_loan_lifecycle.sql. Nothing enforces it but this
 * comment.
 *
 * THREE, NOT FIVE, and the two absences are the decisions worth knowing.
 *
 * There is no `declined`: a refusal is a thing that did NOT happen to the work,
 * there is no date on which possession moved, and `ondo.provenance_entries` is
 * read by everyone who can read the work — permanently, since an entry can only
 * be withdrawn. "This museum considered it and said no" is a claim about an
 * institution's internal decision, not a fact about the object. The refusal is
 * recorded as `ondo.loans.status = 'declined'`, between the two parties.
 *
 * There is no `dispatched`: it is a claim that the work is in transit, which the
 * receipt supersedes within days, and OND-87's list of moments to keep distinct
 * — offer, acceptance, receipt, decline, return — contains no despatch.
 *
 * Null on a `loan` entry means somebody wrote it by hand about the arrangement
 * as a whole, which is what the history modal has always produced.
 */
export const LOAN_HISTORY_ACTS = ["agreed", "received", "returned"];
export function isLoanHistoryAct(value) {
    return (typeof value === "string" &&
        LOAN_HISTORY_ACTS.includes(value));
}
/**
 * Whether the BORROWER has said the object arrived.
 *
 * A timestamp and not a status, because a crate in transit and a picture on the
 * borrower's wall are both `out` — `ondo.work_states()` reads that word to
 * answer `on_loan`, and `loans_one_live` counts it as live. 20261003094000
 * decision 2.
 *
 * Null means NOBODY HAS SAID SO, never that it has not arrived. There is no
 * timer anywhere that could fill it in.
 */
export function isReceived(receivedAt) {
    return receivedAt !== null;
}
//# sourceMappingURL=loan.js.map